// One-shot migration: legacy single-doc localStorage state -> first drawOla
// document in IndexedDB. Rollback-safe: we keep the old localStorage keys
// until we have verified the doc is readable. Idempotent via
// `meta.migrationVersion`.
//
// Existing binaries in the `files-db` used by LocalData are NOT moved here;
// we leave them and let Excalidraw pick them up when the doc loads (they are
// re-emitted through the normal onChange flow and then persisted into our
// `binaries` store with refCount=1).

import { importFromLocalStorage } from "../data/localStorage";

import {
  backfillSceneKeys,
  createDoc,
  getActiveDocId,
  getDoc,
  listDocs,
  setActiveDocId,
  getMigrationVersion,
  setMigrationVersion,
} from "./multiFileStore";
import { repairUsageLedger } from "./usage";

// v1: legacy single-doc localStorage → first IndexedDB doc.
// v2: optional `DocMeta.cloud` binding added; no data rewrite needed (field
//     is optional, existing rows stay valid).
// v3: usage-ledger key unification. `usage.ts` used to READ "drawOlaUsage"
//     while `multiFileStore.ts` WROTE "drawolaUsage", so every ledger read
//     returned 0. Both now agree on `META_KEYS.USAGE`; this step recomputes
//     the ledger from the stored docs and deletes the stale entry. No doc
//     data is touched.
// v4: optional `DocMeta.shares` added; no data rewrite needed (field is
//     optional, existing rows stay valid).
// v5: `DocMeta.sceneKey` added for cloud auto-sync; stamped on existing docs
//     so a bound draw is compared against the cloud once at the next boot.
const CURRENT_VERSION = 5;

/**
 * Version at and above which the legacy single-doc localStorage import has
 * already run. Anything newer is a metadata-only step, so a browser sitting on
 * v2 must NOT be sent back through the v1 import (that would duplicate the
 * starter document).
 */
const LEGACY_IMPORT_DONE_AT = 2;

/**
 * @param defaultDocName localized name for the first/starter document.
 *   Caller resolves it from the active language so "Untitled" is translated.
 */
export async function runMigrations(defaultDocName: string): Promise<{
  migrated: boolean;
  activeDocId: string | null;
}> {
  const v = await getMigrationVersion();

  if (v >= LEGACY_IMPORT_DONE_AT) {
    // Nothing to import. Apply any pending metadata-only steps (v3's ledger
    // repair and v5's sceneKey backfill), then resolve the active doc as
    // before.
    if (v < CURRENT_VERSION) {
      await runLedgerRepair();
      await runSceneKeyBackfill();
      await setMigrationVersion(CURRENT_VERSION);
    }
    const existingActive = await getActiveDocId();
    if (existingActive) {
      return { migrated: false, activeDocId: existingActive };
    }
    // pick the most recently updated doc as active
    const docs = await listDocs();
    if (docs.length) {
      await setActiveDocId(docs[0].id);
      return { migrated: false, activeDocId: docs[0].id };
    }
    return { migrated: false, activeDocId: null };
  }

  // Migration v1: legacy single-doc -> first drawOla document.
  let imported;
  try {
    imported = importFromLocalStorage();
  } catch {
    imported = { elements: [], appState: null };
  }

  const hasContent =
    (imported.elements && imported.elements.length > 0) || imported.appState;

  if (!hasContent) {
    // nothing to migrate; create an empty starter doc only if there are no
    // docs yet (fresh install)
    const docs = await listDocs();
    if (docs.length === 0) {
      const meta = await createDoc(defaultDocName);
      await setActiveDocId(meta.id);
      await runLedgerRepair();
      await setMigrationVersion(CURRENT_VERSION);
      return { migrated: true, activeDocId: meta.id };
    }
    await runLedgerRepair();
    await setMigrationVersion(CURRENT_VERSION);
    const active = docs[0].id;
    await setActiveDocId(active);
    return { migrated: false, activeDocId: active };
  }

  const meta = await createDoc(defaultDocName, {
    elements: imported.elements as readonly any[],
    appState: (imported.appState ?? {}) as any,
  });
  // verify by reading back
  const readBack = await getDoc(meta.id);
  if (!readBack) {
    // do NOT bump migration version; leave legacy keys intact for next retry
    return { migrated: false, activeDocId: null };
  }
  await setActiveDocId(meta.id);
  await runLedgerRepair();
  await setMigrationVersion(CURRENT_VERSION);
  // success — leave the legacy localStorage keys in place for now.
  // We keep them as a safety net. A future cleanup migration can clear
  // them once we trust the new path in production.
  return { migrated: true, activeDocId: meta.id };
}

/**
 * Migration v3, isolated so a failure can never block boot.
 *
 * Runs AFTER any document creation in this pass, so the recomputed total
 * already includes whatever the migration just wrote. If it throws (an
 * IndexedDB hiccup, a browser mid-eviction) the caller still bumps the
 * version: the ledger self-corrects on the next `saveDoc` / `deleteDoc`
 * anyway, and the only cost of the stale legacy key surviving is a few
 * unread bytes.
 */
async function runLedgerRepair(): Promise<void> {
  try {
    await repairUsageLedger();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[drawOla] usage ledger repair failed", e);
  }
}

/**
 * Migration v5, isolated so a failure can never block boot. A doc it missed
 * gets its key on its next save anyway.
 */
async function runSceneKeyBackfill(): Promise<void> {
  try {
    await backfillSceneKeys();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[drawOla] sceneKey backfill failed", e);
  }
}
